import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Upload, FileText, ArrowLeft, GitCompare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";
import { QUOTE_TONE } from "./QuoteDialog";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2,
  maximumFractionDigits: 2 })}`;
const signed = (n) => `${n > 0 ? "+" : ""}${money(n)}`;

/** Load a vendor quote against the purchasing QUEUE (before any PO): pick or drop the quote,
 *  compare it with the FRS/CO lines at estimate cost, then merge or replace the prices. Every
 *  cost variance lands on the budget row's Forecast to Complete. */
export default function BoardQuoteDialog({ project, group, onClose, onApplied }) {
  const [quotes, setQuotes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  const fileRef = useRef(null);
  const manufacturer = group?.manufacturer || "";

  const load = useCallback(async () => {
    try {
      const params = { unattached: true };
      if (manufacturer) params.manufacturer = manufacturer;
      setQuotes((await opsApi.boardQuotes(project.id, params)).quotes);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load the quotes"); }
  }, [project.id, manufacturer]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const res = await opsApi.poQuoteUpload(project.id, "", Array.from(files), manufacturer);
      res.results.forEach((r) => {
        if (r.duplicate) toast.info(r.message);
        else toast.success(`Filed ${r.quote.quote_ref || r.file}`);
      });
      await load();
      const fresh = res.results.find((r) => !r.duplicate)?.quote;
      if (fresh && res.results.length === 1) setPicked(fresh);
    } catch (e) { toast.error(e?.response?.data?.detail || "That file could not be read"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (picked) {
    return <BoardMergePanel project={project} group={group} quote={picked}
                            onBack={() => { setPicked(null); load(); }} onApplied={onApplied} />;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-board-quote-dialog">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Vendor quotes{manufacturer ? ` · ${manufacturer}` : " · all manufacturers"}
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Drop the vendor's e-mail (.eml) or the quote PDF. Prices are compared with the queue
              at estimate cost; what you apply replaces the estimate cost on those lines and the
              gain or fade is pushed to Budgets → Forecast to complete.
            </p>
          </div>
          <input ref={fileRef} type="file" multiple accept=".eml,.pdf,.docx,.txt,.csv" className="hidden"
                 data-testid="ops-board-quote-file" onChange={(e) => upload(e.target.files)} />
          <Button variant="data" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}
                  data-testid="ops-board-quote-upload">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />} Add a quote
          </Button>
        </div>

        {quotes === null ? (
          <p className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
        ) : quotes.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500"
               data-testid="ops-board-quote-empty">
            No quote on file{manufacturer ? ` for ${manufacturer}` : ""} yet — add one above.
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="ops-board-quote-list">
            {quotes.map((q) => (
              <li key={q.id} className="flex flex-wrap items-center gap-2 px-3 py-2.5 text-xs" data-testid={`ops-board-quote-${q.id}`}>
                <FileText className="h-3.5 w-3.5 text-slate-400" />
                <span className="font-semibold text-slate-900">{q.quote_ref || "No quote #"}</span>
                <span className="text-slate-600">{q.vendor || q.manufacturer || "Vendor not read"}</span>
                <span className={`rounded-full px-2 py-0.5 font-medium ${QUOTE_TONE[q.status] || ""}`}>{q.status_label}</span>
                <span className="text-slate-500">{q.quote_date || "—"} · {q.line_count} line{q.line_count === 1 ? "" : "s"}</span>
                <span className="ml-auto font-medium text-slate-800">{q.total ? money(q.total) : "total not read"}</span>
                {["received", "applied"].includes(q.status) && (
                  <Button size="sm" variant="outline" className="h-7" onClick={() => setPicked(q)}
                          data-testid={`ops-board-quote-compare-${q.id}`}>
                    <GitCompare className="mr-1 h-3 w-3" /> Compare &amp; merge
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="ops-board-quote-close">Close</Button>
        </div>
      </div>
    </div>
  );
}


function BoardMergePanel({ project, group, quote, onBack, onApplied }) {
  const [cmp, setCmp] = useState(null);
  const [ticked, setTicked] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    opsApi.boardQuoteCompare(project.id, quote.id, group?.key).then((c) => {
      setCmp(c);
      const t = {};
      c.rows.forEach((r) => { if (r.po_line_id && !r.no_price_on_quote && r.unit_delta !== 0) t[r.po_line_id] = true; });
      setTicked(t);
    }).catch((e) => toast.error(e?.response?.data?.detail || "Could not compare"));
  }, [project.id, quote.id, group?.key]);

  const rows = cmp?.rows.filter((r) => r.po_line_id) || [];
  const matched = rows.filter((r) => !r.no_price_on_quote);
  const chosen = matched.filter((r) => ticked[r.po_line_id]);
  const forecastDelta = chosen.reduce((t, r) => t + (r.budget_delta || 0), 0);

  const apply = async (mode) => {
    const lines = (mode === "replace" ? matched : chosen)
      .map((r) => ({ board_line_id: r.po_line_id, unit_cost: r.quote_unit_price }));
    if (!lines.length) { toast.error("Tick at least one line"); return; }
    setBusy(true);
    try {
      const res = await opsApi.boardQuoteApply(project.id, quote.id, { lines, mode, group_key: group?.key || "" });
      const pushed = res.applied.budget_rows || [];
      const total = res.applied.lines.reduce((t, l) => t + l.delta, 0);
      toast.success(`${res.applied.lines.length} line(s) priced from ${quote.quote_ref || "the quote"} · ${signed(total)} ${
        pushed.length ? `pushed to Forecast to complete on ${pushed.length} budget row(s)` : "forecast variance (no budget row reached)"}`);
      if (res.applied.unbudgeted_delta) toast.info(`${signed(res.applied.unbudgeted_delta)} had no budget row (line has no cost code) — not forecast`);
      onApplied();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be applied"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onBack}>
      <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-board-quote-merge">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="text-slate-500 hover:text-slate-800" data-testid="ops-board-quote-merge-back">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h3 className="text-base font-semibold text-slate-900">
            Queue{group ? ` · ${group.manufacturer} ${group.cost_code || ""}` : ""} vs quote {quote.quote_ref || "(no #)"} · {quote.vendor || quote.manufacturer}
          </h3>
        </div>
        {!cmp ? (
          <p className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Comparing…</p>
        ) : (
          <>
            <div className="mt-3 grid gap-2 text-xs sm:grid-cols-4" data-testid="ops-board-quote-summary">
              <Stat label="Queue at estimate cost" value={money(cmp.summary.board_total)} />
              <Stat label="At quoted prices" value={money(cmp.summary.quoted_total)}
                    sub={<span className={cmp.summary.delta > 0 ? "text-rose-700" : "text-emerald-700"}>{signed(cmp.summary.delta)}</span>} />
              <Stat label="Matched" value={`${cmp.summary.matched} of ${cmp.summary.board_lines}`}
                    sub={cmp.summary.no_price_on_quote ? `${cmp.summary.no_price_on_quote} not priced on the quote` : "every queue line priced"} />
              <Stat label="Forecast push (ticked)" value={signed(forecastDelta)}
                    sub={forecastDelta > 0 ? "fade — cost up" : forecastDelta < 0 ? "gain — cost down" : "no change"} />
            </div>
            {cmp.summary.no_budget_row > 0 && (
              <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="ops-board-quote-no-budget">
                {cmp.summary.no_budget_row} matched line(s) have no cost code, so their variance cannot reach a budget row.
              </p>
            )}

            <table className="mt-3 w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-1.5"></th>
                  <th className="px-2 py-1.5 text-left" colSpan={2}>Queue line</th>
                  <th className="px-2 py-1.5 text-right">Qty</th>
                  <th className="px-2 py-1.5 text-right">Estimate</th>
                  <th className="border-l border-slate-200 px-2 py-1.5 text-left" colSpan={2}>Quote</th>
                  <th className="px-2 py-1.5 text-right">Unit</th>
                  <th className="px-2 py-1.5 text-right">Δ unit</th>
                  <th className="px-2 py-1.5 text-right">Δ forecast</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.po_line_id} data-testid={`ops-board-quote-row-${r.item_number || r.po_line_id}`}
                      className={r.no_price_on_quote ? "text-slate-400" : ""}>
                    <td className="px-2 py-1">
                      {!r.no_price_on_quote && (
                        <input type="checkbox" checked={!!ticked[r.po_line_id]} disabled={!cmp.can_apply}
                               onChange={(e) => setTicked((t) => ({ ...t, [r.po_line_id]: e.target.checked }))}
                               data-testid={`ops-board-quote-tick-${r.item_number || r.po_line_id}`} />
                      )}
                    </td>
                    <td className="px-2 py-1 font-mono">{r.item_number || "—"}</td>
                    <td className="max-w-[260px] truncate px-2 py-1" title={r.description}>
                      {r.description}<span className="ml-1 text-slate-400">{r.cost_code}</span>
                    </td>
                    <td className="px-2 py-1 text-right">{r.po_qty}</td>
                    <td className="px-2 py-1 text-right">{money(r.po_unit_cost)}</td>
                    <td className="border-l border-slate-200 px-2 py-1 font-mono">{r.no_price_on_quote ? "" : (r.quote_item_number || "—")}</td>
                    <td className="max-w-[220px] truncate px-2 py-1" title={r.quote_description}>
                      {r.no_price_on_quote ? <i>not on this quote</i> : r.quote_description}
                      {!r.no_price_on_quote && <span className="ml-1 text-[10px] text-slate-400">by {r.matched_by}</span>}
                    </td>
                    <td className="px-2 py-1 text-right">{r.no_price_on_quote ? "" : money(r.quote_unit_price)}</td>
                    <td className={`px-2 py-1 text-right ${r.unit_delta > 0 ? "text-rose-700" : r.unit_delta < 0 ? "text-emerald-700" : ""}`}>
                      {r.no_price_on_quote ? "" : signed(r.unit_delta)}
                    </td>
                    <td className={`px-2 py-1 text-right ${r.budget_delta > 0 ? "text-rose-700" : r.budget_delta < 0 ? "text-emerald-700" : ""}`}>
                      {r.no_price_on_quote ? "" : signed(r.budget_delta)}
                      {!r.no_price_on_quote && !r.has_budget_row && <span className="ml-1 text-amber-600" title="No cost code — no budget row">!</span>}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={10} className="px-2 py-4 text-center text-slate-500">Nothing on the queue to compare.</td></tr>}
              </tbody>
            </table>

            <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={onBack} data-testid="ops-board-quote-merge-cancel">Back</Button>
              {cmp.can_apply && (
                <>
                  <Button variant="outline" size="sm" onClick={() => apply("replace")} disabled={busy || !matched.length}
                          title="Every matched queue line takes the quoted price" data-testid="ops-board-quote-replace">
                    Replace all ({matched.length})
                  </Button>
                  <Button size="sm" onClick={() => apply("merge")} disabled={busy || !chosen.length} data-testid="ops-board-quote-merge-apply">
                    {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Merge ticked ({chosen.length})
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-slate-200 p-2.5">
      <p className="text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-slate-900">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-500">{sub}</p>}
    </div>
  );
}
